import { CheckIcon } from '@velocity/icons/system';
import { Headline, Text } from '@velocity/ui';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EDevice } from '../../../../redux/sequence/enums';
import { SequenceConfig } from '../../../../redux/sequence/types';
import { RootState } from '../../../../redux/types';
import { Partner, Type } from '../../../../types/instant-lease-api';

type PhaseState = 'active' | 'complete' | 'upcoming';

/*
 * `!` is only used on the Velocity Headline/Text components so their own
 * typography styles don't override the margins/colors set here.
 */
const circleStateClasses: Record<PhaseState, string> = {
  active:
    'bg-(--vel-color-secondary-400) shadow-[0_0_1px_0_rgba(9,30,66,0.2),0_2px_2px_0_rgba(9,30,66,0.08)]',
  complete:
    'bg-(--vel-color-secondary-400) shadow-[0_0_1px_0_rgba(9,30,66,0.2),0_2px_2px_0_rgba(9,30,66,0.08)]',
  upcoming: 'bg-(--vel-color-neutral-300)',
};

const titleStateClasses: Record<PhaseState, string> = {
  active: 'text-(--vel-color-secondary-400)!',
  complete: 'text-(--vel-color-secondary-400)!',
  upcoming: 'text-(--vel-color-secondary-100)!',
};

const subtitleStateClasses: Record<PhaseState, string> = {
  active: 'text-(--vel-color-secondary-400)!',
  complete: 'text-(--vel-color-secondary-100)!',
  upcoming: 'text-(--vel-color-secondary-100)!',
};

interface SequencesPhasesProps {
  config: SequenceConfig[];
  sequence: number;
  partner: Partner;
  step: number;
  type: Type.B2B | Type.B2C;
  /** Rendered below the description of the active phase (e.g. CTA button) */
  action?: React.ReactNode;
}

const SequencesPhases: React.FC<SequencesPhasesProps> = ({
  config,
  sequence,
  partner,
  type,
  action,
}) => {
  // sequence Storage
  const { device } = useSelector((state: RootState) => state.sequence);

  // Dispatch hook
  const dispatch = useDispatch();

  // Phase state
  const getPhaseState = (index: number): PhaseState => {
    if (sequence === index) {
      return 'active';
    }
    if (sequence > index) {
      return 'complete';
    }
    return 'upcoming';
  };

  // Scroll based on phase
  useEffect(() => {
    if (sequence !== 0 && device === EDevice.MOBILE) {
      const previousStep = document.getElementById(
        `step-${sequence === 0 ? 0 : sequence - 1}`,
      );

      previousStep?.scrollIntoView({
        behavior: 'instant' as any,
        block: 'center',
        inline: 'center',
      });

      const step = document.getElementById(`step-${sequence + 1}`);
      setTimeout(() => {
        step?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }, 2000);
    }
  }, [device, dispatch, sequence]);

  // Config based on leasing type
  const currentConfig =
    partner === Partner.ALD_FLEX && type !== Type.B2B
      ? config
      : config.slice(0, config.length - 1);

  // The last entry of the config is never rendered as a phase
  const phases = currentConfig.slice(0, currentConfig.length - 1);

  return (
    <ol id='sequence-steps' className='mx-auto w-full list-none p-0 text-left'>
      {phases.map((seq, index) => {
        const state = getPhaseState(index);
        const isActive = state === 'active';
        const title =
          state === 'complete' ? seq.name : (seq.overviewTitle ?? seq.name);
        const subTitle = seq.overviewSubTitle ?? seq.subTitle;
        const hasAction = isActive && !!action;

        return (
          <li
            id={`step-${index}`}
            key={seq.key}
            className={`flex flex-col last:pb-0 ${
              hasAction ? 'pb-12' : 'pb-18'
            }`}
            aria-current={isActive ? 'step' : undefined}
          >
            <div
              className='relative mb-12 flex items-center justify-center before:absolute before:inset-x-0 before:top-1/2 before:h-px before:bg-(--vel-color-neutral-300) max-md:mb-6'
              aria-hidden='true'
            >
              <span
                className={`relative flex size-15 items-center justify-center rounded-full font-(family-name:--vel-typography-headline-100-font_family) text-[40px] leading-12 font-semibold text-(--vel-color-neutral-white) max-md:size-12 max-md:text-[30px] max-md:leading-9 ${circleStateClasses[state]}`}
              >
                {state === 'complete' ? <CheckIcon size='m' /> : index + 1}
              </span>
            </div>

            <Headline
              component='h2'
              variant={isActive ? '400' : '300'}
              className={`m-0! ${titleStateClasses[state]}`}
            >
              {title}
            </Headline>

            {subTitle && (
              <Text
                component='p'
                variant={isActive ? '300' : '200'}
                loose
                className={`mx-0! mt-3! mb-0! max-lg:mt-1! ${subtitleStateClasses[state]}`}
              >
                {subTitle}
              </Text>
            )}

            {hasAction && (
              <div className='mt-12 flex justify-center'>{action}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
};

export default SequencesPhases;
